/* ============================================================
   Laley PDP Clean v4 — bundle
   Modulær PDP-runtime. Den gamle v3-monolitten er kun historisk arkiv.
   Registrerer metoder på LaleyPDP.prototype via PDP.registerModule.
   Krever pdp-clean-v4-core.js lastet først (defer-rekkefølge i product-clean-v3-1.liquid).
   ============================================================ */
(() => {
  const PDP = window.LaleyPDPCleanV4;
  if (!PDP || typeof PDP.registerModule !== 'function') {
    console.error('[PDP] Kjerne mangler — hopper over modul: bundle');
    return;
  }
  const { cssEscape, escapeHTML, normalizeSearchText } = PDP.utils;

  PDP.registerModule('bundle', {
    formatNorwegianList(items) {
      const list = items.map((item) => String(item || '').trim()).filter(Boolean);
      if (list.length <= 1) return list[0] || '';
      if (list.length === 2) return `${list[0]} og ${list[1]}`;
      return `${list.slice(0, -1).join(', ')} og ${list[list.length - 1]}`;
    },

    bundleSharedSizeInfo(value, sizeGroups = this.bundleSharedSizeGroups || []) {
      const groupList = Array.from(sizeGroups || []);
      const sizeIndexes = groupList
        .map((group) => Number(group.dataset.optionIndex))
        .filter((index) => Number.isFinite(index));
      const sizeIndexSet = new Set(sizeIndexes);
      const selected = this.selectedOptions();
      const matchesNonSizeSelection = (variant) => selected.every((selectedValue, index) => {
        if (!selectedValue || sizeIndexSet.has(index)) return true;
        return variant.options[index] === selectedValue;
      });

      const exactMatches = this.variants.filter((variant) => (
        matchesNonSizeSelection(variant)
        && sizeIndexes.every((index) => variant.options[index] === value)
      ));

      const parts = groupList.map((group) => {
        const index = Number(group.dataset.optionIndex);
        const matchesPart = this.variants.filter((variant) => (
          matchesNonSizeSelection(variant)
          && variant.options[index] === value
        ));
        return {
          index,
          name: this.optionGroupDisplayName(group),
          exists: matchesPart.length > 0,
          available: matchesPart.some((variant) => variant.available),
        };
      });

      const availableParts = parts.filter((part) => part.available);
      const unavailableParts = parts.filter((part) => part.exists && !part.available);

      return {
        value,
        exactExists: exactMatches.length > 0,
        exactAvailable: exactMatches.some((variant) => variant.available),
        availableParts,
        unavailableParts,
        canMix: availableParts.length > 0 && !exactMatches.some((variant) => variant.available),
      };
    },

    bundleNearestAvailableValue(group, wantValue) {
      // Nærmeste kjøpbare størrelse i gruppen; ønsket først, deretter
      // nærmeste med preferanse for mindre (M utsolgt -> S før L).
      const values = Array.from(group.querySelectorAll('[data-pdp-option-input]')).map((input) => input.value);
      const availableAt = (index) => {
        const value = values[index];
        if (value == null) return null;
        const info = this.bundleSharedSizeInfo(value, [group]);
        return info.availableParts.length > 0 ? value : null;
      };
      const wantIndex = values.indexOf(wantValue);
      if (wantIndex === -1) {
        for (let i = 0; i < values.length; i += 1) { const hit = availableAt(i); if (hit) return hit; }
        return null;
      }
      for (let distance = 0; distance < values.length; distance += 1) {
        const below = availableAt(wantIndex - distance);
        if (below) return below;
        if (distance > 0) {
          const above = availableAt(wantIndex + distance);
          if (above) return above;
        }
      }
      return null;
    },

    bundleSharedStatusCopy(value, info = this.bundleSharedSizeInfo(value)) {
      const label = String(value || '').trim();
      if (!label) return { title: '', copy: '' };
      if (info.exactAvailable) return { title: '', copy: '' };
      const availableParts = this.formatNorwegianList(info.availableParts.map((part) => `${part.name} ${label}`));
      const unavailableParts = this.formatNorwegianList(info.unavailableParts.map((part) => part.name));
      if (!info.exactExists) {
        return {
          title: `${label} finnes ikke samlet`,
          copy: 'Miks størrelser og velg størrelse per plagg.',
        };
      }
      if (info.availableParts.length > 0 && info.unavailableParts.length > 0) {
        return {
          title: `${unavailableParts} utsolgt i ${label}`,
          copy: `${availableParts} finnes. Miks størrelser for å kjøpe settet.`,
        };
      }
      if (info.availableParts.length > 1 && info.unavailableParts.length === 0) {
        return {
          title: `${label} kan ikke kjøpes samlet`,
          copy: 'Miks størrelser og velg en tilgjengelig kombinasjon.',
        };
      }
      return {
        title: `${label} er utsolgt`,
        copy: 'Få varsel når størrelsen er tilbake.',
      };
    },

    bundleMixedStatusCopy(variant) {
      if (!variant || variant.available) return null;
      const sharedVisible = this.section.querySelector('[data-pdp-bundle-shared]:not([hidden])');
      if (sharedVisible) return null;
      const groups = Array.from(this.bundleSharedSizeGroups || []);
      if (groups.length < 2) return null;

      const bundleSizeIndexes = this.bundleSizeOptionIndexes();
      const selected = this.selectedOptions();
      const matchesNonBundleSelection = (candidate) => selected.every((selectedValue, index) => {
        if (!selectedValue || bundleSizeIndexes.has(index)) return true;
        return candidate.options[index] === selectedValue;
      });

      const parts = groups.map((group) => {
        const index = Number(group.dataset.optionIndex);
        const value = group.querySelector('[data-pdp-option-input]:checked')?.value || selected[index] || variant.options?.[index] || '';
        const matches = this.variants.filter((candidate) => (
          matchesNonBundleSelection(candidate)
          && candidate.options[index] === value
        ));
        return {
          name: this.optionGroupDisplayName(group),
          value,
          exists: matches.length > 0,
          available: matches.some((candidate) => candidate.available),
        };
      }).filter((part) => part.value);

      const unavailable = parts.filter((part) => part.exists && !part.available);
      if (!unavailable.length) return null;
      const available = parts.filter((part) => part.available);
      const unavailableLabel = this.formatNorwegianList(unavailable.map((part) => `${part.name} ${part.value}`));
      const unavailableNames = this.formatNorwegianList(unavailable.map((part) => part.name));
      const availableLabel = this.formatNorwegianList(available.map((part) => `${part.name} ${part.value}`));
      return {
        title: `${unavailableLabel} er utsolgt`,
        copy: availableLabel
          ? `${availableLabel} finnes. Velg en annen størrelse på ${unavailableNames}.`
          : 'Velg en annen størrelse eller få varsel når den er tilbake.',
      };
    },

    showBundleSharedNote(pill) {
      const note = this.bundleShared?.querySelector('[data-pdp-bundle-shared-note]');
      if (!note) return;
      const title = pill?.dataset.statusTitle || '';
      const copy = pill?.dataset.statusCopy || '';
      if (!title && !copy) {
        note.hidden = true;
        note.removeAttribute('aria-label');
        note.replaceChildren();
        return;
      }
      note.innerHTML = `${title ? `<strong>${escapeHTML(title)}</strong>` : ''}${copy ? `<span>${escapeHTML(copy)}</span>` : ''}`;
      note.setAttribute('aria-label', [title, copy].filter(Boolean).join('. '));
      note.hidden = false;
    },

    hideBundleSharedNote() {
      const note = this.bundleShared?.querySelector('[data-pdp-bundle-shared-note]');
      if (!note) return;
      note.hidden = true;
      note.removeAttribute('aria-label');
      note.replaceChildren();
    },

    bundleSizeOptionIndexes() {
      const groups = Array.from(this.bundleSharedSizeGroups || []);
      if (groups.length < 2) return new Set();
      return new Set(groups
        .map((group) => Number(group.dataset.optionIndex))
        .filter((index) => Number.isFinite(index)));
    },

    async bindBundleAutoDiscover() {
      const bundle = this.bundle;
      if (!bundle || bundle.dataset.pdpBundleAuto !== 'true') return;
      const prefix = (bundle.dataset.bundlePrefix || '').trim();
      const optionsJsonEl = bundle.querySelector('[data-pdp-bundle-options]');
      const tagsJsonEl = bundle.querySelector('[data-pdp-bundle-tags]');
      if (!prefix || !optionsJsonEl) return;
      let optionNames = [];
      try {
        const parsed = JSON.parse(optionsJsonEl.textContent || '[]');
        optionNames = parsed.map((o) => typeof o === 'string' ? o : (o?.name || '')).filter(Boolean);
      } catch (e) { return; }
      // Filter out generisk "Title"-option (1-option-bundles uten meningsfull option-navn)
      optionNames = optionNames.filter(n => n.toLowerCase() !== 'title');

      // Fallback: hvis ingen meningsfulle options, bruk tags som komponent-identifikatorer
      if (!optionNames.length && tagsJsonEl) {
        try {
          const tagsParsed = JSON.parse(tagsJsonEl.textContent || '[]');
          const generic = new Set(['bundle','set','sett','pack','gavesett','beauty','selvpleie','hjem','klær','klar','laley','workout','yoga','training','treningsklær','treningssett','salg','sale','bestselger','nyhet','presale','final-sale']);
          optionNames = tagsParsed.filter(t => typeof t === 'string' && !generic.has(t.toLowerCase()) && t.length > 2);
        } catch (e) { /* ignore */ }
      }
      if (!optionNames.length) return;

      const normalizeSearch = (value) => String(value || '')
        .toLowerCase()
        .replace(/å/g, 'a')
        .replace(/ø/g, 'o')
        .replace(/æ/g, 'ae')
        .replace(/[^a-z0-9]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      const currentHandle = this.section.dataset.productHandle || '';
      const primaryPrefix = prefix.split(/\s+/)[0] || prefix;
      const translatedTerms = (value) => {
        const text = normalizeSearch(value);
        const terms = new Set([value, text]);
        const garmentTerms = [
          'frill shorts',
          'shorts',
          'shirt',
          'skjorte',
          'pants',
          'bukse',
          'bukser',
          'tights',
          'leggings',
          'top',
          'topp',
          'tee',
          't shirt',
          't skjorte',
          'sweater',
          'genser',
          'knit',
          'jakke',
          'jacket',
          'cardigan',
          'vest',
          'dress',
          'kjole',
          'skirt',
          'skjørt',
        ];
        garmentTerms.forEach((term) => {
          const normalizedTerm = normalizeSearch(term);
          if (!normalizedTerm || !` ${text} `.includes(` ${normalizedTerm} `)) return;
          terms.add(term);
          if (normalizedTerm) terms.add(normalizedTerm);
          const lastWord = normalizedTerm.split(' ').filter(Boolean).pop();
          if (lastWord && lastWord.length > 2) terms.add(lastWord);
        });
        const pairs = [
          [/t skjorte|tee shirt|t shirt|t-shirt/g, 'tee'],
          [/shorts?/g, 'shorts'],
          [/bukse|bukser|pants|trouser/g, 'pants'],
          [/genser|sweater|knit/g, 'sweater'],
          [/jakke|jacket/g, 'jacket'],
          [/skjorte|shirt/g, 'shirt'],
          [/tights|leggings/g, 'tights'],
          [/topp|top/g, 'top'],
        ];
        pairs.forEach(([pattern, replacement]) => {
          if (pattern.test(text)) terms.add(text.replace(pattern, replacement));
        });
        return Array.from(terms).map((term) => String(term || '').trim()).filter(Boolean);
      };
      const queryCandidates = (name) => {
        const terms = translatedTerms(name);
        const candidates = new Set();
        terms.forEach((term) => {
          candidates.add(`${prefix} ${term}`);
          candidates.add(`${primaryPrefix} ${term}`);
          candidates.add(`${term} ${primaryPrefix}`);
          candidates.add(term);
        });
        return Array.from(candidates).filter(Boolean);
      };
      const scoreProduct = (product, optionName) => {
        const haystack = normalizeSearch(`${product?.title || ''} ${product?.handle || ''} ${product?.url || ''}`);
        const optionTokens = normalizeSearch(optionName).split(' ').filter((token) => token.length > 2);
        const familyTokens = normalizeSearch(primaryPrefix).split(' ').filter((token) => token.length > 2);
        let score = 0;
        optionTokens.forEach((token) => { if (haystack.includes(token)) score += 4; });
        familyTokens.forEach((token) => { if (haystack.includes(token)) score += 5; });
        if (/set|sett|bundle|pack/.test(haystack)) score -= 12;
        if ((product?.handle || this.handleFromProductUrl(product?.url || '')) === currentHandle) score -= 100;
        return score;
      };
      const fullProductFromSearch = async (product) => {
        const handle = product?.handle || this.handleFromProductUrl(product?.url || '');
        if (!handle || handle === currentHandle) return null;
        const full = await this.fetchProductJson(handle);
        const images = Array.isArray(full?.images) ? full.images : [];
        const image = images.length
          ? images[images.length - 1]
          : (product?.image || product?.featured_image?.url || full?.featured_image || '');
        return {
          handle,
          title: full?.title || product?.title || '',
          url: `/products/${handle}`,
          image: image && String(image).startsWith('//') ? `${window.location.protocol}${image}` : image,
          price: Number(full?.price || (typeof product?.price === 'string' ? Math.round(parseFloat(product.price) * 100) : product?.price) || 0),
        };
      };

      const searchProduct = async (name) => {
        const seenHandles = new Set();
        let best = null;
        let bestScore = -Infinity;
        for (const query of queryCandidates(name)) {
          try {
            const url = `${this.cartUrl('search/suggest.json')}?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last`;
            const r = await fetch(url, { headers: { Accept: 'application/json' } });
            if (!r.ok) continue;
            const data = await r.json();
            const products = data?.resources?.results?.products || [];
            for (const product of products) {
              const handle = product?.handle || this.handleFromProductUrl(product?.url || '');
              if (!handle || seenHandles.has(handle) || handle === currentHandle) continue;
              seenHandles.add(handle);
              const score = scoreProduct(product, name);
              if (score > bestScore) {
                bestScore = score;
                best = product;
              }
            }
            if (bestScore >= 9) break;
          } catch (e) {
            // Try next query candidate.
          }
        }
        if (!best || bestScore < 5) return null;
        return fullProductFromSearch(best);
      };

      const results = await Promise.all(optionNames.map((name) => searchProduct(name)));
      const seen = new Set();
      const found = results.filter((product) => {
        if (!product?.handle || seen.has(product.handle)) return false;
        seen.add(product.handle);
        return true;
      });

      const oldIcons = bundle.querySelector('.pdp-clean__bundle-icons');
      const oldList = bundle.querySelector('.pdp-clean__bundle-pieces');
      const expectedPieces = Number(bundle.dataset.setPieces || 0);
      const piecesCountEl = bundle.querySelector('[data-pdp-bundle-pieces-count]');
      const keepCountFallback = () => {
        if (piecesCountEl && expectedPieces > 0) piecesCountEl.textContent = String(expectedPieces);
      };
      if (!found.length) {
        oldIcons?.remove();
        oldList?.remove();
        keepCountFallback();
        return;
      }
      if (expectedPieces > 1 && found.length < expectedPieces) {
        oldIcons?.remove();
        oldList?.remove();
        keepCountFallback();
        return;
      }

      const list = document.createElement('ul');
      list.className = 'pdp-clean__bundle-pieces';
      list.setAttribute('role', 'list');
      list.setAttribute('aria-label', 'Produkter inkludert i settet');
      found.forEach((bp) => {
        const li = document.createElement('li');
        li.className = 'pdp-clean__bundle-piece';
        const a = document.createElement('a');
        a.className = 'pdp-clean__bundle-piece-link';
        a.href = bp.url;
        a.title = bp.title;
        a.setAttribute('aria-label', bp.title);
        if (bp.image) {
          const img = document.createElement('img');
          img.className = 'pdp-clean__bundle-piece-img';
          img.src = bp.image;
          img.alt = '';
          img.loading = 'lazy';
          img.width = 80;
          img.height = 80;
          a.appendChild(img);
        }
        const span = document.createElement('span');
        span.className = 'pdp-clean__bundle-piece-title';
        span.textContent = bp.title;
        a.appendChild(span);
        li.appendChild(a);
        list.appendChild(li);
      });
      if (oldList) oldList.replaceWith(list);
      else if (oldIcons) oldIcons.replaceWith(list);
      else bundle.querySelector('.pdp-clean__bundle-copy')?.insertAdjacentElement('beforebegin', list);

      if (found.length !== Number(bundle.dataset.setPieces)) {
        bundle.dataset.setPieces = String(found.length);
      }
      this.updateBundlePieceUI(bundle, found);
      this.scheduleBundleMaterialHydration(found);
    },

    bindBundleMaterials() {
      if (!this.bundle) return;
      const pieces = Array.from(this.bundle.querySelectorAll('.pdp-clean__bundle-piece-link')).map((link) => ({
        title: link.getAttribute('title') || link.textContent || '',
        url: link.getAttribute('href') || '',
        handle: this.handleFromProductUrl(link.getAttribute('href') || ''),
      })).filter((piece) => piece.handle || piece.url);
      this.scheduleBundleMaterialHydration(pieces);
    },

    scheduleBundleMaterialHydration(pieces = []) {
      if (!this.bundle || !pieces.length) return;
      const run = () => this.hydrateBundleMaterials(pieces);
      if ('requestIdleCallback' in window) {
        const id = window.requestIdleCallback(run, { timeout: 2200 });
        this.unsubs.push(() => window.cancelIdleCallback?.(id));
      } else {
        const timer = window.setTimeout(run, 900);
        this.timers.push(timer);
      }
    },

    async fetchBundleMaterialText(handle) {
      const normalizedHandle = String(handle || '').trim();
      if (!normalizedHandle) return '';
      if (!this.bundleMaterialCache) this.bundleMaterialCache = new Map();
      if (this.bundleMaterialCache.has(normalizedHandle)) return this.bundleMaterialCache.get(normalizedHandle);

      const textFromHTML = (value) => String(value || '')
        .replace(/<br\s*\/?>/gi, '. ')
        .replace(/<\/(p|li|div|h[1-6])>/gi, '. ')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/\s+/g, ' ')
        .trim();
      const materialFromDescription = (value) => {
        const text = textFromHTML(value);
        if (!text) return '';
        const materialWords = /(?:100\s*%|bomull|lyocell|viskose|polyester|akryl|ull|elastan|spandex|lin|modal|nylon|polyamid|rib[- ]?strikk|strikk|jersey|fleece|canvas|kordfløyel|denim|sateng|silke|mesh|velour|twill)/i;
        const sentences = (text.match(/[^.!?]+[.!?]?/g) || [text])
          .map((sentence) => sentence.replace(/\s+/g, ' ').trim())
          .filter(Boolean);
        const hits = sentences.filter((sentence) => materialWords.test(sentence));
        const picked = (hits.length ? hits : sentences.filter((sentence) => /stoff|material|kvalitet|myk/i.test(sentence))).slice(0, 2);
        return picked.join(' ').slice(0, 260).trim();
      };

      let material = '';
      try {
        const response = await fetch(this.cartUrl(`products/${normalizedHandle}`), { headers: { Accept: 'text/html' } });
        if (response.ok) {
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const row = Array.from(doc.querySelectorAll('[data-pdp-panel="materiale"] .pdp-clean__meta-row')).find((candidate) => {
            const label = normalizeSearchText(candidate.querySelector('.pdp-clean__meta-label')?.textContent || '');
            return label === 'materiale' || label === 'materialer';
          });
          material = row?.querySelector('.pdp-clean__meta-copy')?.textContent?.replace(/\s+/g, ' ').trim() || '';
        }
      } catch (e) {
        material = '';
      }

      if (!material) {
        try {
          const data = await this.fetchProductJson(normalizedHandle);
          material = materialFromDescription(data?.description || '');
        } catch (e) {
          material = '';
        }
      }

      this.bundleMaterialCache.set(normalizedHandle, material);
      return material;
    },

    async hydrateBundleMaterials(pieces = []) {
      const row = this.section.querySelector('[data-pdp-bundle-materials-row]');
      const list = this.section.querySelector('[data-pdp-bundle-materials]');
      if (!row || !list || list.dataset.pdpBundleMaterialsLoaded === 'true') return;

      const rows = await Promise.all(pieces.slice(0, 6).map(async (piece) => {
        const handle = piece.handle || this.handleFromProductUrl(piece.url || '');
        const material = await this.fetchBundleMaterialText(handle);
        if (!material) return null;
        return { title: piece.title || '', material };
      }));
      const validRows = rows.filter(Boolean);
      if (!validRows.length) return;

      list.replaceChildren(...validRows.map((piece) => {
        const item = document.createElement('li');
        item.innerHTML = `<strong>${escapeHTML(piece.title)}</strong><span>${escapeHTML(piece.material)}</span>`;
        return item;
      }));
      list.dataset.pdpBundleMaterialsLoaded = 'true';
      row.hidden = false;
    },

    updateBundlePieceUI(bundle, foundPieces) {
      const piecesCount = foundPieces.length;
      const sumComponents = foundPieces.reduce((sum, p) => sum + (Number(p.price) || 0), 0);
      // Lagre på dataset slik at updateBundle (kalt ved hver variant-change)
      // kan reberegne savings mot current variant.price
      bundle.dataset.bundleComponentSum = String(sumComponents);

      const piecesCountEl = bundle.querySelector('[data-pdp-bundle-pieces-count]');
      if (piecesCountEl) piecesCountEl.textContent = String(piecesCount);

      // Trigger initial savings-render mot current variant
      if (this.currentVariant) this.updateBundle(this.currentVariant);
    },

    bindBundleSizeSync() {
      if (!this.optionGroups || this.optionGroups.length < 2) return;
      const isBundle = !!this.section.querySelector('[data-pdp-bundle]');
      if (!isBundle) return;

      const isSizeGroup = (g) => {
        const legend = (g.querySelector('legend')?.textContent || '').toLowerCase();
        return !/farge|color|colour/.test(legend);
      };
      const sizeGroups = this.optionGroups.filter(isSizeGroup);
      if (sizeGroups.length < 2) return;

      const valuesFor = (group) => Array.from(group.querySelectorAll('[data-pdp-option-input]')).map(i => i.value);
      const firstValues = valuesFor(sizeGroups[0]);
      const sortedKey = firstValues.slice().sort().join('|');
      const allMatch = sizeGroups.every(g => valuesFor(g).sort().join('|') === sortedKey);
      if (!allMatch) return;

      const shared = document.createElement('div');
      // picker-klassen gir pillene NØYAKTIG samme metrikk-regler som enkeltprodukt
      shared.className = 'pdp-clean__picker pdp-clean__bundle-shared';
      shared.setAttribute('data-pdp-bundle-shared', '');
      const pillsHtml = firstValues.map((v) => {
        const safeValue = escapeHTML(v);
        return `
          <button type="button" role="radio" aria-checked="false" class="pdp-clean__option pdp-clean__option--button pdp-clean__bundle-shared-pill" data-value="${safeValue}">
            <span class="pdp-clean__option-name">${safeValue}</span>
            <span class="pdp-clean__option-stock" data-pdp-bundle-shared-pill-status hidden></span>
          </button>
        `;
      }).join('');
      // Samme typografiske grammatikk som enkeltprodukt-velgeren:
      // «Størrelse: Velg» + rolig tekstlenke til høyre (som Størrelsesguide).
      shared.innerHTML = `
        <div class="pdp-clean__picker-label pdp-clean__bundle-shared-head">
          <span>Størrelse: <span class="pdp-clean__picker-label-value" data-pdp-bundle-shared-value>Velg</span></span>
          <button type="button" class="pdp-clean__size-guide pdp-clean__bundle-shared-mix" data-pdp-bundle-mix aria-label="Miks størrelser og velg størrelse per plagg">Velg per plagg</button>
        </div>
        <div class="pdp-clean__picker-options pdp-clean__bundle-shared-pills" role="radiogroup" aria-label="Felles størrelse for hele settet">${pillsHtml}</div>
        <p class="pdp-clean__bundle-shared-note" data-pdp-bundle-shared-note role="status" hidden></p>
      `;
      this.optionGroups[0].insertAdjacentElement('beforebegin', shared);

      this.bundleShared = shared;
      this.bundleSharedSizeGroups = sizeGroups;
      sizeGroups.forEach((g) => g.hidden = true);

      const sharedPills = Array.from(shared.querySelectorAll('.pdp-clean__bundle-shared-pill'));
      const mixToggle = shared.querySelector('[data-pdp-bundle-mix]');
      this.updateBundleSharedState();

      // Auto-fallback (Sondre juli-26): når INGEN felles størrelse er kjøpbar
      // (delene finnes bare i ulike størrelser) er den delte velgeren en
      // blindvei — en vegg av UTSOLGT selv om plaggene kan kjøpes hver for
      // seg. Åpne per-plagg-valget direkte i stedet.
      const anySharedAvailable = firstValues.some((value) => {
        const info = this.bundleSharedSizeInfo(value, sizeGroups);
        return info.exactExists && info.exactAvailable;
      });
      if (!anySharedAvailable) {
        shared.hidden = true;
        sizeGroups.forEach((g) => { g.hidden = false; });
        this.updateVariantStatus?.(this.currentVariant);
        return;
      }

      sharedPills.forEach((pill) => {
        this.on(pill, 'click', () => {
          if (pill.disabled) return;
          const value = pill.dataset.value;
          if (pill.dataset.canMix === 'true') {
            // Sondre juli-26: delt størrelse som er delvis utsolgt skal ikke
            // være en blindvei — åpne per-plagg, velg ønsket størrelse der
            // den finnes og nærmeste tilgjengelige på resten.
            shared.hidden = true;
            sizeGroups.forEach((g) => { g.hidden = false; });
            this.hideBundleSharedNote();
            sizeGroups.forEach((group) => {
              const target = this.bundleNearestAvailableValue(group, value);
              if (!target) return;
              const match = group.querySelector(`[data-pdp-option-input][value="${cssEscape(target)}"]`);
              if (match && !match.disabled) {
                match.checked = true;
                match.dispatchEvent(new Event('change', { bubbles: true }));
              }
            });
            this.updateVariantStatus?.(this.currentVariant);
            return;
          }
          sharedPills.forEach((p) => {
            const selected = p === pill;
            p.classList.toggle('is-selected', selected);
            p.setAttribute('aria-checked', selected ? 'true' : 'false');
          });
          const sharedValue = shared.querySelector('[data-pdp-bundle-shared-value]');
          if (sharedValue) sharedValue.textContent = value;
          if (pill.dataset.soldout === 'true' || pill.dataset.impossible === 'true') this.showBundleSharedNote(pill);
          else this.hideBundleSharedNote();
          sizeGroups.forEach((group) => {
            const match = group.querySelector(`[data-pdp-option-input][value="${cssEscape(value)}"]`);
            if (match && !match.disabled) {
              match.checked = true;
              match.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        });
      });

      this.on(mixToggle, 'click', () => {
        shared.hidden = true;
        sizeGroups.forEach((g) => g.hidden = false);
        this.hideBundleSharedNote();
        this.updateVariantStatus(this.currentVariant);
      });
    },

    updateBundleSharedState() {
      const shared = this.bundleShared || this.section.querySelector('[data-pdp-bundle-shared]');
      const sizeGroups = this.bundleSharedSizeGroups || [];
      if (!shared || !sizeGroups.length) return;

      shared.querySelectorAll('.pdp-clean__bundle-shared-pill').forEach((pill) => {
        const value = pill.dataset.value || '';
        const info = this.bundleSharedSizeInfo(value, sizeGroups);
        const copy = this.bundleSharedStatusCopy(value, info);
        const soldOut = info.exactExists && !info.exactAvailable;
        const impossible = !info.exactExists;
        const status = pill.querySelector('[data-pdp-bundle-shared-pill-status]');

        pill.classList.toggle('is-unavailable', (soldOut || impossible) && !info.canMix);
        pill.dataset.soldout = soldOut && !info.canMix ? 'true' : 'false';
        pill.dataset.impossible = impossible && !info.canMix ? 'true' : 'false';
        pill.dataset.canMix = info.canMix ? 'true' : 'false';
        pill.dataset.statusTitle = copy.title || '';
        pill.dataset.statusCopy = copy.copy || '';
        pill.title = copy.title && copy.copy ? `${copy.title}. ${copy.copy}` : (copy.title || '');
        pill.disabled = impossible && !info.canMix;
        pill.setAttribute('aria-disabled', pill.disabled ? 'true' : 'false');

        if (status) {
          if (info.canMix) {
            // Delvis tilgjengelig: klikk åpner per-plagg med nærmeste
            // størrelse — pillen skal ikke se ut som en utsolgt-vegg.
            status.textContent = '';
            status.hidden = true;
          } else {
            status.textContent = soldOut ? 'Utsolgt' : (impossible ? 'Ikke mulig' : '');
            status.hidden = !(soldOut || impossible);
          }
        }

        if (pill.classList.contains('is-selected')) {
          if (soldOut || impossible) this.showBundleSharedNote(pill);
          else this.hideBundleSharedNote();
        }
      });
    },

    // «Kjøp som sett»-kortet server-rendres nå i sections/product-clean-v3-1.liquid
    // fra metafeltet custom.set_parent (synket fra Shopify Bundles-appen).
    // Den gamle klient-oppdagelsen (tittel-ord -> search/suggest) ga falske
    // positiver (f.eks. Ear Muff White -> 90's underwear set white) og er fjernet.

    updateBundle(variant) {
      if (!this.bundle || !variant) return;
      // Variant-pris oppdateres slik at savings beregnes mot riktig pris
      this.bundle.dataset.bundlePrice = variant.price;

      // Manuell compare-cents fra metafelt vinner over auto-discover sum
      // (lar merchant overstyre Shopify Bundles auto-pris-syncing)
      const manualCompare = Number(this.bundle.dataset.bundleCompareCents || 0);
      const autoSum = Number(this.bundle.dataset.bundleComponentSum || 0);
      const reference = manualCompare > 0 ? manualCompare : autoSum;
      const savings = reference > 0 ? (reference - variant.price) : 0;

      // Bundle headline under pris: bekreft settverdi uten å gjøre det til en alternativ CTA.
      const headline = this.section.querySelector('[data-pdp-bundle-headline]');
      const headlineAmount = this.section.querySelector('[data-pdp-bundle-headline-amount]');
      if (headline && headlineAmount) {
        if (savings > 0) {
          headlineAmount.textContent = this.money(savings);
          headline.replaceChildren(document.createTextNode('Settpris · du sparer '), headlineAmount);
          headline.hidden = false;
        } else {
          headline.hidden = true;
        }
      }
      // Bundle-copy: vis kun pieces-count (savings vises i headline øverst, ingen duplikater)
      const copyEl = this.bundle.querySelector('[data-pdp-bundle-copy]');
      const piecesCount = Number(this.bundle.dataset.setPieces || 0);
      if (copyEl) {
        if (savings > 0) {
          // Skjul duplikat-tekst når savings-headline allerede vises
          copyEl.hidden = true;
        } else {
          copyEl.hidden = false;
          copyEl.innerHTML = `<span data-pdp-bundle-pieces-count>${piecesCount}</span> deler inkludert`;
        }
      }
    },
  });
})();
